/**
 * widgets/Sidebar.ts · 13 角色侧边栏 (按 RBAC 白名单生成)
 *
 * 替代 v200.x sidebar 混乱:
 *   - v187 Phase B 白名单失效 → v300 直接从 rbac.ts 读
 *   - 不让 sales 看到 13 部门 chip
 *   - 不让 worker 看到 maintenance 页面
 *
 * 设计:
 *   - 按角色 rbac.getRolePages() 渲染
 *   - 当前页高亮
 *   - 移动端可折叠 (≤767px hidden by default · 顶 hamburger 触发)
 */

import type { User } from '@core/auth';
import { getRolePages } from '@core/rbac';

export interface PageMeta {
  icon: string;
  zh: string;
  vi: string;
  section: string;
}

/**
 * 页面元数据字典 (替代 v200.16.1 pageNames 4654 条混乱字典)
 *
 * 团队 W2 补全所有 99 页 (当前仅 starter 示范)
 */
export const PAGES: Record<string, PageMeta> = {
  // Home
  gmHome: { icon: '🎯', zh: '总经理首页', vi: 'Trang chủ GM', section: '主页' },
  hrHome: { icon: '👥', zh: '人事首页', vi: 'Trang HR', section: '主页' },
  financeHome: { icon: '💰', zh: '财务首页', vi: 'Trang TC', section: '主页' },
  salesHome: { icon: '📋', zh: '销售首页', vi: 'Trang BH', section: '主页' },
  engineerHome: { icon: '⚙️', zh: '工程首页', vi: 'Trang KT', section: '主页' },
  supervisorHome: { icon: '👷', zh: '生产主管', vi: 'Trưởng SX', section: '主页' },
  workerHome: { icon: '👨‍🏭', zh: '工人台', vi: 'Công nhân', section: '主页' },
  qualityHome: { icon: '🛡️', zh: '品控首页', vi: 'QC', section: '主页' },
  warehouseHome: { icon: '📦', zh: '仓储首页', vi: 'Kho', section: '主页' },
  maintHome: { icon: '🔧', zh: '维修首页', vi: 'Bảo trì', section: '主页' },
  purchasingHome: { icon: '🛒', zh: '采购首页', vi: 'Mua hàng', section: '主页' },
  guardHome: { icon: '🛡️', zh: '保卫首页', vi: 'Bảo vệ', section: '主页' },
  isoHome: { icon: '📜', zh: 'ISO 首页', vi: 'ISO', section: '主页' },

  // 销售流 (W2-W3 已实现)
  quotationCreate: { icon: '➕', zh: '新建报价', vi: 'Tạo báo giá', section: '销售' },
  quotationList: { icon: '📋', zh: '报价列表', vi: 'DS báo giá', section: '销售' },
  salesOrderList: { icon: '🧾', zh: '销售订单', vi: 'Đơn hàng BH', section: '销售' },
  order360: { icon: '🔍', zh: '订单全景', vi: 'Đơn 360°', section: '销售' },
  customerList: { icon: '👥', zh: '客户管理', vi: 'KH', section: '销售' },
  customerCreate: { icon: '➕', zh: '新建客户', vi: 'Thêm KH', section: '销售' },

  // 财务 (W2-W3 已实现 5)
  customerPaymentCreate: { icon: '💵', zh: '录收款', vi: 'Thu tiền', section: '财务' },
  customerPayments: { icon: '📒', zh: '收款记录', vi: 'Sổ thu', section: '财务' },
  arInvoiceList: { icon: '📄', zh: 'AR 发票', vi: 'Hóa đơn AR', section: '财务' },
  arAging: { icon: '📊', zh: 'AR 账龄', vi: 'Tuổi nợ AR', section: '财务' },
  apInvoiceList: { icon: '📃', zh: 'AP 发票', vi: 'Hóa đơn AP', section: '财务' },  // ⭐ v324
  apAging: { icon: '📈', zh: 'AP 账龄', vi: 'Tuổi nợ AP', section: '财务' },
  profitLoss: { icon: '📉', zh: '损益表', vi: 'L/L', section: '财务' },
  monthlyOverhead: { icon: '💼', zh: '月度间接费', vi: 'Chi phí gián tiếp', section: '财务' },

  // 仓储/采购/品控/生产 (团队 W3 补)
  inventoryList: { icon: '📋', zh: '库存清单', vi: 'Tồn kho', section: '仓储' },
  productList: { icon: '📦', zh: '产品管理', vi: 'SP', section: '工程' },
  poList: { icon: '🛒', zh: '采购订单', vi: 'PO', section: '采购' },
  qcRecords: { icon: '📋', zh: 'QC 记录', vi: 'QC log', section: '品控' },
  workshopScreen: { icon: '📺', zh: '车间大屏', vi: 'Màn hình XS', section: '生产' },
  bigScreenProduction: { icon: '🏭', zh: '生产大屏', vi: 'BĐ SX lớn', section: '生产' },   // ⭐ v357
  bigScreenData: { icon: '📊', zh: '数据大屏', vi: 'BĐ dữ liệu', section: '生产' },         // ⭐ v357

  // 生产 PMC (v326 · Sprint 8-D · supervisor 主战场)
  ganttSchedule: { icon: '📅', zh: 'Gantt 排程', vi: 'Lịch Gantt', section: '生产' },
  productionKanban: { icon: '🏭', zh: '生产看板', vi: 'Bảng SX', section: '生产' },
  woManage: { icon: '🔧', zh: '工单管理', vi: 'Quản lý LSX', section: '生产' },
  woTracking: { icon: '📍', zh: '工单跟踪', vi: 'Theo dõi LSX', section: '生产' },
  woCreate: { icon: '➕', zh: '新建工单', vi: 'Tạo LSX', section: '生产' },
  woDetail: { icon: '🔍', zh: '工单详情', vi: 'CT LSX', section: '生产' },
  woEdit: { icon: '✏️', zh: '编辑工单', vi: 'Sửa LSX', section: '生产' },
  ipqcInline: { icon: '🔍', zh: 'IPQC 在制检', vi: 'IPQC', section: '品控' },
  oeeCenter: { icon: '📊', zh: 'OEE 设备效率', vi: 'OEE', section: '生产' },

  // v340 新页
  leaveRequestCreate: { icon: '📝', zh: '新建请假', vi: 'Tạo nghỉ phép', section: '人事' },

  // v335 大批量 36 新页
  // 财务深扩 (v330)
  apInvoiceCreate: { icon: '➕', zh: '录 AP 票', vi: 'Tạo HĐ AP', section: '财务' },
  arInvoiceDetail: { icon: '🔍', zh: 'AR 票详情', vi: 'CT HĐ AR', section: '财务' },
  financePipeline: { icon: '💎', zh: '财务流水线', vi: 'Quy trình TC', section: '财务' },
  voucherList: { icon: '📒', zh: '凭证列表', vi: 'DS chứng từ', section: '财务' },
  voucherCreate: { icon: '➕', zh: '凭证录入', vi: 'Tạo chứng từ', section: '财务' },
  bankAccounts: { icon: '🏦', zh: '银行账户', vi: 'TK ngân hàng', section: '财务' },
  reconciliation: { icon: '🔗', zh: '银行对账', vi: 'Đối soát NH', section: '财务' },
  monthlyProfitReport: { icon: '📈', zh: '月利润', vi: 'LN tháng', section: '财务' },
  expenseHRReview: { icon: '👥', zh: '报销 HR 审', vi: 'HR duyệt CP', section: '财务' },
  expenseFinanceReview: { icon: '💰', zh: '报销财审', vi: 'TC duyệt CP', section: '财务' },
  // 仓储深扩 (v331)
  rawMaterialQuery: { icon: '🔍', zh: '原料查询', vi: 'Tra NVL', section: '仓储' },
  mrpDashboard: { icon: '📐', zh: 'MRP 看板', vi: 'MRP', section: '仓储' },
  inventoryAdjust: { icon: '📊', zh: '库存盘点', vi: 'Kiểm kho', section: '仓储' },
  inventoryTransfer: { icon: '🔁', zh: '库存调拨', vi: 'Chuyển kho', section: '仓储' },
  logisticsCenter: { icon: '🚚', zh: '物流中心', vi: 'Logistics', section: '仓储' },
  stockByWarehouse: { icon: '🏬', zh: '分仓库存', vi: 'Tồn theo kho', section: '仓储' },
  finishedProductInventory: { icon: '📦', zh: '成品进销存', vi: 'Tồn TP', section: '仓储' },
  // 工人移动端 (v332)
  myWages: { icon: '💰', zh: '我的工资', vi: 'Lương của tôi', section: '工人' },
  workerAttendance: { icon: '🕐', zh: '考勤打卡', vi: 'Chấm công', section: '工人' },
  workerRepair: { icon: '🛠️', zh: '我要报修', vi: 'Báo hỏng', section: '工人' },
  workerDashboard: { icon: '👷', zh: '工人台', vi: 'Bảng CN', section: '工人' },
  // 保卫 (v334)
  guardScanIn: { icon: '📥', zh: '入门扫码', vi: 'Quét vào', section: '保卫' },
  guardScanOut: { icon: '📤', zh: '出门扫码', vi: 'Quét ra', section: '保卫' },
  guardHistory: { icon: '📋', zh: '入出门日志', vi: 'Lịch sử cổng', section: '保卫' },
  guardPatrolDashboard: { icon: '🚶', zh: '巡逻看板', vi: 'BĐ tuần tra', section: '保卫' },
  guardPatrolScan: { icon: '📍', zh: '巡逻打卡', vi: 'Quẹt tuần tra', section: '保卫' },
  guardGateControl: { icon: '🚪', zh: '闸机控制', vi: 'Cổng', section: '保卫' },
  // 生产补全 (v335) · oeeCenter/woTracking/woEdit 已在 v326 加, 此处仅 qcDashboard
  qcDashboard: { icon: '🛡️', zh: 'QC 仪表板', vi: 'BĐ QC', section: '品控' },

  // 工程部 (v329 · Sprint 8-C)
  bomTreeEditor: { icon: '🌲', zh: 'BOM 树', vi: 'Cây BOM', section: '工程' },
  processManage: { icon: '⚙️', zh: '工艺管理', vi: 'Quy trình', section: '工程' },
  kitBomManage: { icon: '📦', zh: '套盒 BOM', vi: 'BOM bộ', section: '工程' },
  altMaterialList: { icon: '🔄', zh: '替代物料', vi: 'NVL thay thế', section: '工程' },
  corrugatedRecipeList: { icon: '📐', zh: '瓦楞配方', vi: 'CT giấy sóng', section: '工程' },
  artworkReviewQueue: { icon: '🎨', zh: '图稿审核', vi: 'Duyệt thiết kế', section: '工程' },
  cadLibrary: { icon: '📁', zh: 'CAD 库', vi: 'Thư viện CAD', section: '工程' },
  cadUpload: { icon: '📤', zh: 'CAD 上传', vi: 'Tải CAD', section: '工程' },
  productCreate: { icon: '➕', zh: '新建产品', vi: 'Tạo SP', section: '工程' },              // ⭐ v349
  orderMaterials: { icon: '📐', zh: '订单用料', vi: 'NVL đơn hàng', section: '工程' },        // ⭐ v350 算料
  markCodeManage: { icon: '🏷️', zh: '唛头管理', vi: 'Mã ký hiệu', section: '工程' },           // ⭐ v350 唛头

  // 采购部 (v349 新)
  supplierCreate: { icon: '🏢', zh: '新建供应商', vi: 'Tạo NCC', section: '采购' },          // ⭐ v349
  rfqList: { icon: '💬', zh: '询价单', vi: 'DS RFQ', section: '采购' },                       // ⭐ v349
  purchasingReport: { icon: '📊', zh: '采购报表', vi: 'BC mua hàng', section: '采购' },       // ⭐ v349

  // 维修部 (v327 · Sprint 8-J)
  maintenanceList: { icon: '🔧', zh: '维修工单', vi: 'LS bảo trì', section: '维修' },
  maintenanceRequest: { icon: '🛠️', zh: '设备报修', vi: 'Báo hỏng', section: '维修' },
  equipmentList: { icon: '🏭', zh: '设备清单', vi: 'TB máy', section: '维修' },
  sparePartsManage: { icon: '🔩', zh: '备件管理', vi: 'Phụ tùng', section: '维修' },
  maintenancePlans: { icon: '📅', zh: '预防维修计划', vi: 'KH bảo trì', section: '维修' },    // ⭐ v349
  maintenanceSchedule: { icon: '🗓️', zh: '维修排程', vi: 'Lịch bảo trì', section: '维修' },   // ⭐ v349
  tpmCenter: { icon: '🏆', zh: 'TPM 中心', vi: 'Trung tâm TPM', section: '维修' },             // ⭐ v350 TPM 8 大支柱
  equipmentByDept: { icon: '🏭', zh: '设备按部门', vi: 'TB theo bộ phận', section: '维修' },     // ⭐ v352 真实 34 机型

  // 客诉 + RMA (v323 · Sprint 8-F)
  complaintsCenter: { icon: '🚨', zh: '客诉中心', vi: 'Khiếu nại', section: '品控' },
  complaintCreate: { icon: '🆕', zh: '新建客诉', vi: 'Tạo KN', section: '品控' },
  returnsList: { icon: '📦', zh: '退货管理', vi: 'Trả hàng', section: '品控' },
  returnCreate: { icon: '🆕', zh: '新建退货', vi: 'Tạo RMA', section: '品控' },

  // 销售 360°/Pipeline (v321 · Sprint 8-B)
  mySalesPipeline: { icon: '🛍️', zh: '我的销售管道', vi: 'Pipeline', section: '销售' },
  customer360: { icon: '🔍', zh: '客户全景', vi: 'KH 360°', section: '销售' },

  // 系统
  notifications: { icon: '🔔', zh: '通知', vi: 'Thông báo', section: '系统' },
  // ⚠️ 瘦身清单 #49 删: helpCenter (0 内容装饰)
};

/**
 * v356 · Sidebar collapse 状态 · localStorage 持久化
 * - 'collapsed' = icon-only 56px (默认)
 * - 'expanded' = 完整 192px
 */
type SidebarState = 'collapsed' | 'expanded';
const STORAGE_KEY = 'tn_sidebar_state';

function getSidebarState(): SidebarState {
  try {
    return (localStorage.getItem(STORAGE_KEY) as SidebarState) || 'collapsed';
  } catch { return 'collapsed'; }
}

function setSidebarState(s: SidebarState): void {
  try { localStorage.setItem(STORAGE_KEY, s); } catch { /* */ }
}

export function renderSidebar(user: User, currentPage: string): HTMLElement {
  const state = getSidebarState();
  const aside = document.createElement('aside');
  aside.className = `sidebar bg-white border-r border-gray-200 overflow-y-auto hidden md:block ${state === 'collapsed' ? 'w-14' : 'w-48'}`;
  aside.dataset.role = user.role;
  aside.dataset.state = state;
  aside.style.transition = 'width 0.18s ease-out';

  // hover 临时展开 (when collapsed) · 不持久化
  aside.addEventListener('mouseenter', () => {
    if (aside.dataset.state === 'collapsed') {
      aside.classList.remove('w-14');
      aside.classList.add('w-48');
      aside.dataset.hoverExpand = '1';
    }
  });
  aside.addEventListener('mouseleave', () => {
    if (aside.dataset.hoverExpand === '1') {
      aside.classList.remove('w-48');
      aside.classList.add('w-14');
      delete aside.dataset.hoverExpand;
    }
  });

  const allowedPages = getRolePages(user.role);
  const sections = groupBySection(allowedPages);
  const lang = document.documentElement.lang.startsWith('vi') ? 'vi' : 'zh';

  // Toggle 按钮 (顶部 sticky)
  const toggleArrow = state === 'collapsed' ? '→' : '←';
  let html = `
    <div class="sticky top-0 bg-white z-10 border-b border-gray-100">
      <button class="tn-sidebar-toggle w-full text-center text-gray-500 hover:text-blue-600 hover:bg-gray-50 py-2 text-sm transition-colors" title="${state === 'collapsed' ? '展开 sidebar / Mở rộng' : '折叠 sidebar / Thu gọn'}" aria-label="toggle sidebar">${toggleArrow}</button>
    </div>
    <div class="py-2">
  `;

  for (const [sectionName, pages] of sections) {
    if (state === 'expanded') {
      html += `<div class="tn-sec-label px-3 py-1 text-xs text-gray-500 font-semibold uppercase">${escapeHtml(sectionName)}</div>`;
    } else {
      // collapsed: 显细分隔线 + 1 字符 (按 section 首字符 emoji 取代)
      html += `<div class="tn-sec-divider mx-2 my-1 border-t border-gray-100"></div>`;
    }
    for (const page of pages) {
      const meta = PAGES[page];
      if (!meta) continue;
      const label = lang === 'vi' ? meta.vi : meta.zh;
      const active = currentPage === page;
      if (state === 'expanded') {
        html += `
          <a href="#${page}" class="flex items-center gap-2 px-3 py-1.5 text-sm ${active ? 'bg-blue-50 text-blue-700 font-semibold border-r-2 border-blue-600' : 'text-gray-700 hover:bg-gray-50'}">
            <span class="text-base">${meta.icon}</span>
            <span>${escapeHtml(label)}</span>
          </a>
        `;
      } else {
        // collapsed: 仅 icon + 鼠标 hover 显原生 tooltip
        html += `
          <a href="#${page}" class="tn-icon-link flex items-center justify-center px-2 py-2 text-xl ${active ? 'bg-blue-50 border-l-2 border-blue-600' : 'hover:bg-gray-50'}" title="${escapeHtml(label)}">
            ${meta.icon}
          </a>
        `;
      }
    }
  }
  html += '</div>';

  aside.innerHTML = html;

  // toggle button click handler
  const toggleBtn = aside.querySelector<HTMLButtonElement>('.tn-sidebar-toggle');
  if (toggleBtn) {
    toggleBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const newState: SidebarState = aside.dataset.state === 'collapsed' ? 'expanded' : 'collapsed';
      setSidebarState(newState);
      // 重 render (触发 hashchange refresh)
      location.reload();
    });
  }

  return aside;
}

function groupBySection(pages: string[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const page of pages) {
    const meta = PAGES[page];
    if (!meta) continue;
    if (!map.has(meta.section)) map.set(meta.section, []);
    map.get(meta.section)!.push(page);
  }
  return map;
}

function escapeHtml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]!));
}
